import { Badge, Button, Collapse } from '@vrplatform/components';
import { Icon } from '@vrplatform/icons';
import type { LazyTableProps, MRT_ColumnDef } from '@vrplatform/table';
import { formatCurrency, sum, utc } from '@vrplatform/utils';
import { Box, Center, Group, Text, Tooltip, rem } from '@mantine/core';
import type { currency_enum } from '@vrplatform/graphql';
import { useMemo } from 'react';
import type { Reservation } from '../_queries';
import { useExpenseDetailDrawer } from '../../expense-drawer';
import { useAddExpenseNavigate } from '../../../views/expenses/edit/useAddReservationExpense';
import { DrawerCollapsableTable } from '../../_components';
import { LazyTable } from '@vrplatform/table/src/LazyTable';
import { ReservationSectionJumpTo } from './ReservationSectionJumpTo';
import { RESERVATION_ENTITY_SECTION_ROW_LIMIT } from '../_utils/reservation-entity-section-row-limit';

export const ReservationExpensesSection = ({
  expenses,
  currency,
  reservationId,
  type,
  goToTab,
}: {
  expenses: Reservation['expenses'];
  currency: Reservation['currency'];
  reservationId: string;
  type: 'tab' | 'collapse';
  goToTab: () => void;
}) => {
  const { open: openExpense } = useExpenseDetailDrawer();

  const gotoAddView = useAddExpenseNavigate();
  const total =
    sum(
      expenses.filter((x) => x.status === 'active'),
      'centTotal'
    ) / 100;

  const columns = useMemo<MRT_ColumnDef<Reservation['expenses'][0]>[]>(
    () => [
      {
        header: 'Description',
        accessorKey: 'description',
        Footer: () => (
          <Text span fw={500}>
            Total Expenses
          </Text>
        ),
        mantineTableFooterCellProps: {
          pl: 'xs',
        },
        Cell: ({ row }) => {
          const line = row.original;

          const td = line.status === 'inactive' ? 'line-through' : undefined;

          return (
            <Box>
              <Group gap={rem(8)}>
                <Text lineClamp={2} td={td}>
                  {line.description}
                </Text>
                {line.party === 'owners' && (
                  <Badge size="xs">Billed to owner</Badge>
                )}
              </Group>
              <Text c="gray" size="xs" td={td}>
                {line.expense.date ? utc(line.expense.date).format('LL') : ''}
              </Text>
            </Box>
          );
        },
      },
      {
        header: 'Badge',
        maxSize: 0,
        minSize: 120,
        size: 120,
        Cell: ({ row }) => {
          const expense = row.original.expense;

          return (
            <Tooltip label={expense.description}>
              <Group
                wrap="nowrap"
                gap={5}
                sx={(theme) => ({
                  backgroundColor: theme.colors.gray[1],
                  border: `1px solid ${theme.colors.gray[2]}`,
                  paddingInline: rem(6),
                  paddingBlock: rem(3),
                  borderRadius: theme.radius.xl,
                })}
              >
                <Icon icon="MinusCircleIcon" size={14} />
                <Text span size="xs" maw={100} truncate>
                  Expense
                </Text>
              </Group>
            </Tooltip>
          );
        },
      },
      {
        header: 'amount',
        accessorKey: 'centTotal',
        maxSize: 0,
        minSize: 90,
        size: 90,
        mantineTableBodyCellProps: {
          align: 'right',
        },
        mantineTableFooterCellProps: {
          align: 'right',
          pr: 'xs',
        },
        Cell: ({ row }) => {
          return formatCurrency(
            row.original.centTotal / 100,
            row.original.expense.currency
          );
        },
        Footer: () => {
          return (
            <Text span fw={500} ta="right" display={'block'}>
              {formatCurrency(total, currency)}
            </Text>
          );
        },
      },
    ],
    [total, currency]
  );

  const title = 'Expenses';

  const rightSection = (
    <Button
      leftIcon="PlusIcon"
      color="neutral"
      variant="light"
      size="xs"
      onClick={() =>
        gotoAddView({
          currency: currency as currency_enum,
          reservationId,
        })
      }
    >
      Add expense
    </Button>
  );

  const onRowClick: LazyTableProps<
    Reservation['expenses'][0]
  >['table']['onRowClick'] = {
    handler: (row) => {
      if (row.original.expense.id) openExpense(row.original.expense.id, 'push');
    },
    disabled: (row) => !row.original.expense.id,
  };

  if (type === 'tab') {
    return (
      <>
        <Collapse
          title={
            <Text component="span" size="sm" fw={500}>
              {title}
              <Text c={'gray'} ml="xs" size="xs" component="span">
                {expenses.length}
              </Text>
            </Text>
          }
          rightSection={rightSection}
          hideToggle
        />
        <LazyTable
          data={{
            rows: expenses,
            error: null,
            loading: false,
            rowCount: expenses.length,
          }}
          table={{
            columns,
            hideTopBar: true,
            hideHeader: true,
            enableStickyFooter: true,
            onRowClick,
          }}
        />
      </>
    );
  }

  if (expenses.length > RESERVATION_ENTITY_SECTION_ROW_LIMIT)
    return (
      <ReservationSectionJumpTo
        title={title}
        count={expenses.length}
        goToTab={goToTab}
      />
    );

  return (
    <DrawerCollapsableTable
      title={'Expenses'}
      rightSection={rightSection}
      rowData={expenses}
      columns={columns}
      hideToggle={!expenses.length}
      defaultOpened={!!expenses.length}
      onRowClick={onRowClick}
      emptyRowsFallback={() => (
        <Center>
          <Text size="sm" c="gray">
            No expenses found
          </Text>
        </Center>
      )}
    />
  );
};
